// @ts-nocheck
import pendo from '../../assets/pendo';

/**
 * Pendo Analytics Service
 *
 * Centralized service for Pendo initialization, user identification, and event tracking.
 *
 * Key Features:
 * - Initializes Pendo with user and account data
 * - Handles script loading and initialization state
 * - Provides event tracking for user actions
 * - Supports page view tracking for SPA navigation
 *
 * @namespace Services.Pendo
 */

let pendoInitialized = false;
let pendoScriptLoaded = false;

/**
 * Load script dynamically without React hooks
 * @param {string} scriptContent - Script content to inject
 */
const loadScript = (scriptContent) => {
  const script = document.createElement('script');
  script.text = scriptContent;
  script.async = true;
  document.head.appendChild(script);
};

/**
 * Initialize Pendo with user and account information
 *
 * @param {Object} userSession - User session data
 * @param {string} userSession.userId - User ID
 * @param {string} userSession.email - User email
 * @param {string} userSession.firstName - User first name
 * @param {string} userSession.lastName - User last name
 * @param {Object} tenant - Tenant/account data
 * @param {string} tenant.tenantid - Tenant ID
 * @param {string} tenant.name - Tenant name
 * @param {string} tenant.domain - Tenant domain
 * @returns {Promise<boolean>} - True if initialization successful
 */
export const initializePendo = async (userSession, tenant) => {
  try {
    // Only initialize in production environment
    if (!import.meta.env.PROD) {
      console.log(
        '[Pendo] Skipping initialization - not in production environment'
      );
      return false;
    }

    // Validate required data
    if (!userSession?.userId || !tenant?.tenantid) {
      console.warn('[Pendo] Missing required user or tenant data');
      return false;
    }

    // Load Pendo script if not already loaded
    if (!pendoScriptLoaded) {
      loadScript(pendo);
      pendoScriptLoaded = true;
      await waitForPendo();
    }

    // Initialize Pendo with user and account data
    if (window.pendo) {
      window.pendo.initialize({
        visitor: {
          id: String(userSession.userId), // Pendo requires string IDs
          email: userSession.email,
          firstName: userSession.firstName,
          lastName: userSession.lastName,
          fullName:
            `${userSession.firstName || ''} ${userSession.lastName || ''}`.trim()
        },
        account: {
          id: String(tenant.tenantid), // Pendo requires string IDs
          name: tenant.name,
          domain: tenant.domain
        }
      });

      pendoInitialized = true;
      return true;
    }

    return false;
  } catch (error) {
    console.error('[Pendo] Initialization error:', error);
    return false;
  }
};

/**
 * Update user identification in Pendo
 * Useful when user data changes without full re-initialization
 *
 * @param {Object} userSession - Updated user session data
 * @param {Object} tenant - Updated tenant data
 * @returns {boolean} - True if update successful
 */
export const identifyUser = (userSession, tenant) => {
  try {
    if (!pendoInitialized || !window.pendo) {
      console.warn('[Pendo] Cannot identify user - Pendo not initialized');
      return false;
    }

    window.pendo.identify({
      visitor: {
        id: String(userSession.userId), // Pendo requires string IDs
        email: userSession.email,
        firstName: userSession.firstName,
        lastName: userSession.lastName,
        fullName:
          `${userSession.firstName || ''} ${userSession.lastName || ''}`.trim()
      },
      account: {
        id: String(tenant.tenantid), // Pendo requires string IDs
        name: tenant.name,
        domain: tenant.domain
      }
    });

    return true;
  } catch (error) {
    console.error('[Pendo] User identification error:', error);
    return false;
  }
};

/**
 * Track a page view in Pendo
 * Use this for manual page tracking in SPA navigation
 *
 * @param {string} pageName - Name/path of the page
 * @param {Object} metadata - Additional metadata about the page
 * @returns {boolean} - True if tracking successful
 */
export const trackPageView = (pageName, metadata = {}) => {
  try {
    if (!pendoInitialized || !window.pendo) {
      return false;
    }

    window.pendo.pageLoad({
      page: pageName,
      ...metadata
    });

    return true;
  } catch (error) {
    console.error('[Pendo] Page view tracking error:', error);
    return false;
  }
};

/**
 * Track a custom event in Pendo
 *
 * @param {string} eventName - Name of the event
 * @param {Object} metadata - Event metadata/properties
 * @returns {boolean} - True if tracking successful
 */
export const trackEvent = (eventName, metadata = {}) => {
  try {
    if (!pendoInitialized || !window.pendo) {
      return false;
    }

    window.pendo.track(eventName, metadata);
    return true;
  } catch (error) {
    console.error('[Pendo] Event tracking error:', error);
    return false;
  }
};

/**
 * Check if Pendo is initialized
 *
 * @returns {boolean} - True if Pendo is initialized
 */
export const isPendoInitialized = () => {
  return pendoInitialized && window.pendo !== undefined;
};

/**
 * Wait for Pendo script to load
 *
 * @param {number} timeout - Maximum time to wait in milliseconds
 * @returns {Promise<boolean>} - True if Pendo loaded successfully
 */
const waitForPendo = (timeout = 5000) => {
  return new Promise((resolve) => {
    const startTime = Date.now();

    const checkPendo = () => {
      if (window.pendo) {
        resolve(true);
      } else if (Date.now() - startTime > timeout) {
        console.warn('[Pendo] Script load timeout');
        resolve(false);
      } else {
        setTimeout(checkPendo, 100);
      }
    };

    checkPendo();
  });
};

/**
 * Validate Pendo installation (for debugging)
 * Call window.pendo.validateInstall() in browser console
 */
export const validateInstall = () => {
  if (window.pendo) {
    window.pendo.validateInstall();
  } else {
    console.warn('[Pendo] Not loaded - cannot validate installation');
  }
};
